import React, { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { FaCamera, FaTimes, FaBarcode, FaQrcode, FaPlus, FaCheck, FaTrash } from 'react-icons/fa';

const BarcodeScanner = ({ 
  onComplete, 
  onClose, 
  expectedCount = 1,
  title = "扫描条码/二维码",
  isUnitInbound = false,  
  unitName = "瓶"
}) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanMode, setScanMode] = useState('barcode'); // 'barcode' | 'qrcode'
  const [manualInput, setManualInput] = useState('');
  const [pendingItems, setPendingItems] = useState([]);
  const [scanLevel, setScanLevel] = useState(isUnitInbound ? 'unit' : 'box');
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const videoRef = useRef(null);
  const codeReader = useRef(null);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      videoRef.current.srcObject = stream;
      
      codeReader.current.decodeFromVideoDevice(
        null,
        videoRef.current,
        (result, err) => {
          if (result) {
            handleCodeScanned(result.text);
          }
          if (err && !(err instanceof NotFoundException)) {
            console.error(err);
          }
        }
      );
    } catch (err) {
      setError('无法访问摄像头');
      console.error(err);
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      codeReader.current.reset();
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const handleCodeScanned = (code) => {
      if (scanLevel === 'unit') {
        handleUnitCodeScanned(code);
        return;
      }

      // 检查是否已存在相同的条码
      const existingItemIndex = pendingItems.findIndex(item => 
        item.barcode === code || item.qrcode === code
      );
      
      if (existingItemIndex >= 0) {
        setError(`该${scanMode === 'barcode' ? '条码' : '二维码'}已存在`);
        return;
      }
      
      // 查找当前正在处理的项目
      let targetIndex = pendingItems.findIndex(item => 
        (scanMode === 'barcode' && !item.barcode) || 
        (scanMode === 'qrcode' && !item.qrcode)
      );
      
      if (targetIndex === -1) {
        // 创建新项目
        targetIndex = pendingItems.length;
      }
      
      const updatedItems = [...pendingItems];
      
      // 如果项目不存在，创建新项目
      if (!updatedItems[targetIndex]) {
        updatedItems[targetIndex] = {
          id: Date.now() + targetIndex,
          barcode: '',
          qrcode: '',
          units: [],
          timestamp: new Date().toLocaleTimeString(),
          boxIndex: targetIndex + 1
        };
      }
      
      // 更新对应的码
      if (scanMode === 'barcode') {
        updatedItems[targetIndex].barcode = code;
      } else {
        updatedItems[targetIndex].qrcode = code;
      }
      
      setPendingItems(updatedItems);
      setError(null);
      if (scanMode === 'barcode') {
        setError(`第${targetIndex + 1}${isUnitInbound ? unitName : '箱'}条码已记录，可继续扫描二维码或下一${isUnitInbound ? unitName : '箱'}条码`);
      } else {
        setError(`第${targetIndex + 1}${isUnitInbound ? unitName : '箱'}二维码已记录，可继续扫描条码或下一${isUnitInbound ? unitName : '箱'}条码`);
      }
      
      // 检查是否所有箱都至少有条码（条码为必须，二维码可选）
      const completedBoxes = pendingItems.filter(item => item.barcode).length;
      if (completedBoxes >= expectedCount) {
        setError(`已完成所有 ${expectedCount} ${isUnitInbound ? unitName : '箱'}的条码扫描`);
      }
      
      // 扫描成功后自动停止扫描器
      stopScanning();
  };

  const handleUnitCodeScanned = (code) => {
    // 如果没有箱子，创建散货箱并立即添加当前扫描的瓶码
    if (pendingItems.length === 0) {
      const firstUnit = {
        id: Date.now(),
        barcode: scanMode === 'barcode' ? code : '',
        qrcode: scanMode === 'qrcode' ? code : '',
        timestamp: new Date().toLocaleTimeString()
      };
      
      const looseBox = {
        id: `LOOSE-${Date.now()}`,
        barcode: `LOOSE-${Date.now()}`,
        qrcode: '',
        units: [firstUnit],
        timestamp: new Date().toLocaleTimeString(),
        boxIndex: 1,
        isLooseBox: true
      };
      
      setPendingItems([looseBox]);
      setCurrentBoxIndex(0);
      setError(`散货箱已创建，第1瓶${scanMode === 'barcode' ? '条码' : '二维码'}已记录`);
      stopScanning();
      return;
    }
    
    const currentBox = pendingItems[currentBoxIndex];
    if (!currentBox) {
      setError('请先选择要添加瓶条码的箱');
      return;
    }
    
    // 检查当前箱的瓶条码中是否已存在
    const existingUnit = currentBox.units.find(unit => 
      unit.barcode === code || unit.qrcode === code
    );
    
    if (existingUnit) {
      setError(`该瓶${scanMode === 'barcode' ? '条码' : '二维码'}已存在`);
      stopScanning();
      return;
    }
    
    const updatedItems = [...pendingItems];
    const currentBoxUnits = [...currentBox.units];
    
    // 查找未完成的瓶单元或创建新的
    let targetUnitIndex = currentBoxUnits.findIndex(unit => 
      (scanMode === 'barcode' && !unit.barcode) || 
      (scanMode === 'qrcode' && !unit.qrcode)
    );
    
    if (targetUnitIndex === -1) {
      targetUnitIndex = currentBoxUnits.length;
      currentBoxUnits.push({
        id: Date.now() + targetUnitIndex,
        barcode: '',
        qrcode: '',
        timestamp: new Date().toLocaleTimeString()
      });
    }
    
    // 更新瓶条码/二维码
    if (scanMode === 'barcode') {
      currentBoxUnits[targetUnitIndex].barcode = code;
    } else {
      currentBoxUnits[targetUnitIndex].qrcode = code;
    }
    
    updatedItems[currentBoxIndex].units = currentBoxUnits;
    setPendingItems(updatedItems);
    
    setError(`第${currentBoxIndex + 1}箱第${targetUnitIndex + 1}瓶${scanMode === 'barcode' ? '条码' : '二维码'}已记录`);

    stopScanning();
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleCodeScanned(manualInput.trim());
      setManualInput('');
    }
  };

  const removeItem = (id) => {
    setPendingItems(pendingItems.filter(item => item.id !== id));
  };

  const switchScanMode = (mode) => {
    setScanMode(mode);
    setError(null);
  };

  const handleConfirmAll = () => {
    const maxLength = Math.max(
      Math.max(...pendingItems.map(item => item.boxIndex || 0)),
      pendingItems.length
    );
    
    const barcodes = Array(maxLength).fill('');
    const qrcodes = Array(maxLength).fill('');
    const boxUnits = Array(maxLength).fill(null).map(() => ({ unit_barcodes: [], unit_qrcodes: [] }));
    
    pendingItems.forEach(item => {
      const index = (item.boxIndex || 1) - 1;
      
      // 对于散货箱，不返回箱码，只返回瓶码数据
      if (!item.isLooseBox) {
        if (item.barcode) barcodes[index] = item.barcode;
        if (item.qrcode) qrcodes[index] = item.qrcode;
      }
      
      // 处理瓶级别的条码/二维码
      if (item.units && item.units.length > 0) {
        const unitBarcodes = [];
        const unitQrcodes = [];
        
        item.units.forEach(unit => {
          if (unit.barcode) unitBarcodes.push(unit.barcode);
          if (unit.qrcode) unitQrcodes.push(unit.qrcode);
        });
        
        boxUnits[index] = {
          unit_barcodes: unitBarcodes,
          unit_qrcodes: unitQrcodes
        };
      }
    });
    
    const isLooseBox = pendingItems.length > 0 && pendingItems[0].isLooseBox;
    
    onComplete({
      barcodes: isLooseBox ? [] : barcodes.filter(code => code), 
      qrcodes: isLooseBox ? [] : qrcodes.filter(code => code),
      boxUnits: boxUnits,   
      items: pendingItems,
      isLooseBox: isLooseBox
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl h-full max-h-[95vh] sm:max-h-[90vh] md:max-h-[85vh] lg:max-h-[80vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* 左侧：扫描区域 */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => switchScanMode('barcode')}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    scanMode === 'barcode' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FaBarcode className="mr-2" />
                  扫条码
                </button>
                <button
                  onClick={() => switchScanMode('qrcode')}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    scanMode === 'qrcode' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FaQrcode className="mr-2" />
                  扫二维码
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => setScanLevel('box')}
                  disabled={isUnitInbound}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    scanLevel === 'box' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  📦 扫箱码
                </button>
                <button
                  onClick={() => setScanLevel('unit')}
                  className={`flex items-center px-4 py-2 rounded-lg ${
                    scanLevel === 'unit' 
                      ? 'bg-orange-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  disabled={false}
                >
                  🍷 扫瓶码
                </button>
              </div>
              
              {scanLevel === 'unit' && (
                <div className="mb-4 p-3 bg-orange-50 rounded-lg">
                  {pendingItems.length === 0 ? (
                    <div className="text-sm text-gray-700">
                      📋 散货模式：直接扫描瓶条码，系统将自动创建散货箱
                    </div>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        选择要添加瓶条码的箱：
                      </label>
                      <select
                        value={currentBoxIndex}
                        onChange={(e) => setCurrentBoxIndex(parseInt(e.target.value))}
                        className="w-full p-2 border rounded"
                      >
                        {pendingItems.map((item, index) => (
                          <option key={item.id} value={index}>
                            {item.isLooseBox ? '📋 散货箱' : `第${item.boxIndex}箱`} 
                            {item.barcode && !item.isLooseBox ? `(${item.barcode.slice(-6)})` : ''}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              )}

              {!scanning ? (
                <div className="space-y-3">
                  <button
                    onClick={startScanning}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg flex items-center justify-center"
                  >
                    <FaCamera className="mr-2" />
                    开始扫描{scanMode === 'barcode' ? '条码' : '二维码'}
                  </button>
                  
                  {expectedCount > 1 && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                      <p className="font-medium mb-1">扫描提示：</p>
                      <ul className="text-xs space-y-1">
                        <li>• 建议先扫描条码，再扫描对应的二维码</li>
                        <li>• 系统会自动配对同一{isUnitInbound ? unitName : '箱'}的条码和二维码</li>
                        <li>• 总共需要扫描 {expectedCount} {isUnitInbound ? unitName : '箱'}</li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <video ref={videoRef} className="w-full rounded-lg" />
                  <button
                    onClick={stopScanning}
                    className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded"
                  >
                    停止
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                    正在扫描{scanMode === 'barcode' ? '条码' : '二维码'}
                  </div>
                </div>
              )}
              
              {/* 手动输入 */}
              <form onSubmit={handleManualSubmit} className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  手动输入{scanMode === 'barcode' ? '条码' : '二维码'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    className="flex-1 p-2 border rounded"
                    placeholder={`输入${scanMode === 'barcode' ? '条码' : '二维码'}`}
                  />
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    <FaPlus />
                  </button>
                </div>
              </form>
            </div>
            
            {/* 右侧：待添加列表 */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold">
                  待添加列表 ({pendingItems.length}/{expectedCount})
                </h4>
                {pendingItems.length > 0 && (
                  <button
                    onClick={() => setPendingItems([])}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    清空全部
                  </button>
                )}
              </div>
              
              <div className="space-y-2 max-h-64 sm:max-h-96 overflow-y-auto">
                {pendingItems.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="text-4xl mb-2">📦</div>
                    <p>还没有扫描任何码</p>
                    <p className="text-sm">开始扫描或手动输入</p>
                  </div>
                ) : (
                  pendingItems.map((item, index) => (
                    <div key={item.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 flex justify-between">
                            <span>第{item.boxIndex}箱 - {item.timestamp}</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              item.barcode && item.qrcode ? 'bg-green-100 text-green-700' :
                              item.barcode || item.qrcode ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {item.barcode && item.qrcode ? '已完成' :
                              item.barcode || item.qrcode ? '部分完成' : '待扫描'}
                            </span>
                          </div>
                          
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center">
                              <FaBarcode className={`mr-2 ${item.barcode ? 'text-blue-600' : 'text-gray-400'}`} />
                              <span className="text-sm font-mono">
                                {item.barcode || (
                                  <button
                                    onClick={() => switchScanMode('barcode')}
                                    className="text-blue-600 hover:underline text-xs"
                                  >
                                    点击扫描条码
                                  </button>
                                )}
                              </span>
                            </div>
                            
                            <div className="flex items-center">
                              <FaQrcode className={`mr-2 ${item.qrcode ? 'text-green-600' : 'text-gray-400'}`} />
                              <span className="text-sm font-mono">
                                {item.qrcode || (
                                  <button
                                    onClick={() => switchScanMode('qrcode')}
                                    className="text-green-600 hover:underline text-xs"
                                  >
                                    点击扫描二维码
                                  </button>
                                )}
                              </span>
                            </div>
                          </div>
                          {/* 瓶条码/二维码显示 */}
                          {item.units && item.units.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-medium text-gray-600">
                                  瓶条码 ({item.units.length})
                                </span>
                                <button
                                  onClick={() => {
                                    setScanLevel('unit');
                                    setCurrentBoxIndex(index);
                                  }}
                                  className="text-xs text-orange-600 hover:text-orange-800"
                                >
                                  + 添加瓶码
                                </button>
                              </div>
                              <div className="space-y-1">
                                {item.units.map((unit, unitIndex) => (
                                  <div key={unit.id} className="text-xs bg-white p-2 rounded border">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        {unit.barcode && (
                                          <span className="flex items-center">
                                            <FaBarcode className="mr-1 text-blue-500" size={10} />
                                            <span className="font-mono">{unit.barcode.slice(-6)}</span>
                                          </span>
                                        )}
                                        {unit.qrcode && (
                                          <span className="flex items-center">
                                            <FaQrcode className="mr-1 text-green-500" size={10} />
                                            <span className="font-mono">{unit.qrcode.slice(-6)}</span>
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() => {
                                          const updatedItems = [...pendingItems];
                                          updatedItems[index].units = updatedItems[index].units.filter((_, i) => i !== unitIndex);
                                          setPendingItems(updatedItems);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <FaTimes size={10} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* 添加瓶条码按钮 */}
                          {(!item.units || item.units.length === 0) && item.barcode && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <button
                                onClick={() => {
                                  setScanLevel('unit');
                                  setCurrentBoxIndex(index);
                                }}
                                className="text-xs text-orange-600 hover:text-orange-800 flex items-center"
                              >
                                🍷 添加瓶条码/二维码
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-red-600 hover:text-red-800 ml-2"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* 扫描进度提示 */}
              {expectedCount > 1 && (
                <div className="mt-4 bg-blue-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center text-sm">
                    <span>扫描进度:</span>
                    <span className="font-medium">
                      {pendingItems.filter(item => item.barcode || item.qrcode).length} / {expectedCount} {isUnitInbound ? unitName : '箱'}完成
                    </span>
                  </div>
                  <div className="mt-2 bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(pendingItems.filter(item => item.barcode || item.qrcode).length / expectedCount) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* 底部确认按钮 */}
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3 sm:space-x-3 flex-shrink-0">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 w-full sm:w-auto"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmAll}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center disabled:opacity-50 w-full sm:w-auto"
                  disabled={pendingItems.length === 0}
                >
                  <FaCheck className="mr-2" />
                  确认添加 ({pendingItems.filter(item => item.barcode || item.qrcode).length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;